import { useRouter } from "next/router";
import PropTypes from "prop-types";
import dayjs from "dayjs";

import SEO from "components/data-display/SEO/SEO.component";
import Inventory from "features/inventory/Inventory.component";

import useInventoryTitle from "hooks/inventory/useInventoryTitle.hook";
import useInventoryDescription from "hooks/inventory/useInventoryDescription.hook";
import { getPathnameItem } from "helpers/inventory-filters";

import { defaultLocation } from "data/defaultLocation";
import { wrapper } from "store";
import { getVehicleSpecs } from "services/v1-slices/vehicle-specs-slice";
import { vinnV1Api } from "services/vinn-api-v1";

export default function Cars({ pageProps }) {
  const { location, specs } = pageProps;
  const router = useRouter();
  const title = useInventoryTitle(true);
  const metaDescription = useInventoryDescription();

  return (
    <>
      <SEO
        title={title}
        description={metaDescription}
        url={`https://www.vinnauto.com${router.asPath}`}
        canonical={`https://www.vinnauto.com${router.asPath.split("?")[0]}`}
        ogType="website"
      />
      <Inventory location={location} specs={specs} />
    </>
  );
}
export const getServerSideProps = wrapper.getServerSideProps(
  (store) =>
    async ({ req, params }) => {
      store.dispatch(getVehicleSpecs.initiate());
      // wait for request to be called on the server
      const [specs] = await Promise.all(
        store.dispatch(vinnV1Api.util.getRunningQueriesThunk()),
      );

      const { slug } = params;
      const makes = specs.data.makes.map((make) => make.name);
      const make = getPathnameItem(slug, "make");
      const models = specs.data.models.map((model) => model.name);
      const model = getPathnameItem(slug, "model");
      const year = getPathnameItem(slug, "year");
      if (
        (make && !makes.includes(make)) || // make isn't a valid make
        (model && !models.includes(model)) || // model isn't a valid model
        // year is before 1900 or after year + 1
        (year && (year < 1900 || year > dayjs().year() + 1))
      ) {
        return {
          notFound: true, // 404 if invalid make/model/year, add city/prov in future to this??
        };
      }
      // if city/prov in path name, return those as location
      if (getPathnameItem(slug, "city")) {
        return {
          props: {
            location: {
              city: getPathnameItem(slug, "city"),
              province: getPathnameItem(slug, "province"),
            },
            specs,
          },
        };
      }
      // else return ip location
      if (req.headers["x-vercel-ip-country"] === "CA") {
        return {
          props: {
            location: {
              city: req.headers["x-vercel-ip-city"],
              country: req.headers["x-vercel-ip-country"],
              province: req.headers["x-vercel-ip-country-region"],
              lat: Number(req.headers["x-vercel-ip-latitude"]),
              lng: Number(req.headers["x-vercel-ip-longitude"]),
            },
            specs,
          },
        };
      }
      // else return default location
      return {
        props: {
          location: defaultLocation,
          specs,
        },
      };
    },
);

Cars.propTypes = {
  geo: PropTypes.shape({
    city: PropTypes.string,
    country: PropTypes.string,
    province: PropTypes.string,
    latitude: PropTypes.number,
    longitude: PropTypes.number,
  }),
};

Cars.defaultProps = {
  geo: defaultLocation,
};
